import { Patient } from '../components/patients/PatientCard';

export interface PatientDocument {
  id?: number;
  patient_id: number;
  patient?: Patient;
  title: string;
  description?: string;
  document_type: 'medical_report' | 'lab_result' | 'imaging' | 'dicom' | 'xray_image' | 'prescription' | 'consent_form' | 'insurance_document' | 'referral_letter' | 'medical_certificate' | 'discharge_summary' | 'other';
  document_category?: string; // ✅ This is the subcategory field
  file_name: string;
  original_file_name?: string;
  file_path: string;
  file_size: number;
  file_type: string;
  mime_type: string;
  is_confidential: boolean;
  uploaded_by: number;
  uploaded_by_name?: string;
  upload_date: string;
  related_appointment_id?: number;
  related_prescription_id?: number;
  related_lab_requisition_id?: number;
  tags?: string[];
  notes?: string;
  access_level: 'public' | 'medical_staff' | 'doctor_only' | 'admin_only';
  is_active: boolean;
  thumbnail_path?: string;
  download_count: number;
  last_accessed_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateDocumentData {
  patient_id: number;
  title: string;
  description?: string;
   document_type: 'medical_report' | 'lab_result' | 'imaging' | 'dicom' | 'xray_image' | 'prescription' | 'consent_form' | 'insurance_document' | 'referral_letter' | 'medical_certificate' | 'discharge_summary' | 'other';
  document_category?: string; // ✅ Add this - Laravel accepts it
  document_date?: string;
  subcategory?: string;
  is_confidential: boolean;
  appointment_id?: number;
  related_appointment_id?: number;
  related_prescription_id?: number;
  related_lab_requisition_id?: number;
  tags?: string[];
  notes?: string;
  access_level: 'public' | 'medical_staff' | 'doctor_only' | 'admin_only';
  file: File;
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class DocumentService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.BASE_URL}${url}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Get documents with filtering
  static async getDocuments(params?: {
    patient_id?: number;
    category?: string;
    subcategory?: string;
    is_confidential?: boolean;
    access_level?: string;
    uploaded_by?: number;
    date_from?: string;
    date_to?: string;
    search?: string;
    tags?: string[];
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<PatientDocument>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(`${key}[]`, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const url = `/patient-documents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  // Get single document
  static async getDocument(id: number): Promise<PatientDocument> {
    const result = await this.request(`/patient-documents/${id}`);
    return result.data;
  }

  // Upload document
  static async uploadDocument(data: CreateDocumentData): Promise<PatientDocument> {
    const formData = new FormData();
    
    // Append file first
    formData.append('file', data.file);
    
    // Append other data
    formData.append('patient_id', data.patient_id.toString());
    formData.append('title', data.title);
    formData.append('document_type', data.document_type);
    formData.append('is_confidential', data.is_confidential.toString());
    formData.append('access_level', data.access_level);
    
    if (data.description) formData.append('description', data.description);
    if (data.subcategory) formData.append('subcategory', data.subcategory);
    if (data.related_appointment_id) formData.append('related_appointment_id', data.related_appointment_id.toString());
    if (data.related_prescription_id) formData.append('related_prescription_id', data.related_prescription_id.toString());
    if (data.related_lab_requisition_id) formData.append('related_lab_requisition_id', data.related_lab_requisition_id.toString());
    if (data.notes) formData.append('notes', data.notes);
    
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach(tag => formData.append('tags[]', tag));
    }

    const result = await this.request('/patient-documents', {
      method: 'POST',
      body: formData,
    });
    
    return result.data;
  }

  // Update document metadata
  static async updateDocument(id: number, data: Partial<CreateDocumentData>): Promise<PatientDocument> {
    const updateData: any = {};
    
    if (data.title) updateData.title = data.title;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.document_type) updateData.category = data.document_type;
    if (data.subcategory !== undefined) updateData.subcategory = data.subcategory;
    if (data.is_confidential !== undefined) updateData.is_confidential = data.is_confidential;
    if (data.access_level) updateData.access_level = data.access_level;
    if (data.related_appointment_id !== undefined) updateData.related_appointment_id = data.related_appointment_id;
    if (data.related_prescription_id !== undefined) updateData.related_prescription_id = data.related_prescription_id;
    if (data.related_lab_requisition_id !== undefined) updateData.related_lab_requisition_id = data.related_lab_requisition_id;
    if (data.notes !== undefined) updateData.notes = data.notes;
    if (data.tags !== undefined) updateData.tags = data.tags;

    const result = await this.request(`/patient-documents/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    
    return result.data;
  }

  // Delete document
  static async deleteDocument(id: number): Promise<void> {
    await this.request(`/patient-documents/${id}`, {
      method: 'DELETE',
    });
  }

  // Download document
  
static async downloadDocument(id: number): Promise<{ blob: Blob; filename: string }> {
  const token = localStorage.getItem('auth_token');
  
  const response = await fetch(`${this.BASE_URL}/patient-documents/${id}/download`, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download document');
  }

  const blob = await response.blob();
  let filename = `document_${id}`;
  
  // ✅ Better filename extraction
  const contentDisposition = response.headers.get('Content-Disposition');
  if (contentDisposition) {
    // Handle both quoted and unquoted filenames
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    
    if (matches && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
      
      // ✅ Handle URL encoded filenames
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {
        // If decode fails, try to decode URI component parts
        try {
          filename = decodeURI(filename);
        } catch (e2) {
          // Keep original if all decoding fails
        }
      }
    }
  }

  return { blob, filename };
}

  static async getSecureViewUrl(id: number): Promise<string> {
  const result = await this.request(`/patient-documents/${id}/view`);
  return result.data.secure_url;
}

// Update the existing viewDocument method:
static async viewDocument(id: number): Promise<string> {
  return await this.getSecureViewUrl(id);
}
// Add this method to your DocumentService class in documentService.ts:
static async getDocumentViewData(id: number): Promise<{
  document: PatientDocument;
  secure_url: string;
  expires_at: string;
}> {
  const result = await this.request(`/patient-documents/${id}/view`);
  return result.data;
}
  // Get patient's documents
  static async getPatientDocuments(patientId: number): Promise<PatientDocument[]> {
    const result = await this.request(`/patient-documents?patient_id=${patientId}`);
    return result.data;
  }

  // Get document statistics
  static async getStatistics(): Promise<{
    total_documents: number;
    documents_by_category: Record<string, number>;
    confidential_documents: number;
    recent_uploads: number;
    total_size_mb: number;
  }> {
    const result = await this.request('/patient-documents/statistics');
    return result.data;
  }

  // Batch upload documents
  static async uploadMultipleDocuments(
    patientId: number,
    files: File[],
    metadata: Omit<CreateDocumentData, 'patient_id' | 'file'>
  ): Promise<PatientDocument[]> {
    const uploadPromises = files.map(file => 
      this.uploadDocument({
        ...metadata,
        patient_id: patientId,
        file,
        title: metadata.title || file.name,
      })
    );

    return Promise.all(uploadPromises);
  }

  // Helper methods
  // In documentService.ts, update these methods:
static getCategoryColor(documentType: string): string {
  switch (documentType) {
    case 'lab_result': return '#3b82f6';
    case 'xray_image': return '#8b5cf6';
    case 'prescription': return '#10b981';
    case 'medical_report': return '#f59e0b';
    case 'consent_form': return '#06b6d4';
    case 'insurance_document': return '#84cc16';
    case 'referral_letter': return '#ec4899';
    case 'medical_certificate': return '#10b981';
    case 'discharge_summary': return '#ef4444';
    case 'other': return '#6b7280';
    default: return '#6b7280';
  }
}

static getCategoryLabel(documentType: string): string {
  switch (documentType) {
    case 'lab_result': return 'Lab Result';
    case 'xray_image': return 'X-Ray Image';
    case 'prescription': return 'Prescription';
    case 'medical_report': return 'Medical Report';
    case 'consent_form': return 'Consent Form';
    case 'insurance_document': return 'Insurance Document';
    case 'referral_letter': return 'Referral Letter';
    case 'medical_certificate': return 'Medical Certificate';
    case 'discharge_summary': return 'Discharge Summary';
    case 'other': return 'Other';
    default: return 'Unknown';
  }
}

static getCategoryIcon(documentType: string): string {
  switch (documentType) {
    case 'lab_result': return '🧪';
    case 'xray_image': return '📷';
    case 'prescription': return '💊';
    case 'medical_report': return '📋';
    case 'consent_form': return '📝';
    case 'insurance_document': return '🛡️';
    case 'referral_letter': return '📄';
    case 'medical_certificate': return '🏆';
    case 'discharge_summary': return '📃';
    case 'other': return '📄';
    default: return '📄';
  }
}
  static getAccessLevelColor(accessLevel: string): string {
    switch (accessLevel) {
      case 'public': return '#10b981';
      case 'medical_staff': return '#3b82f6';
      case 'doctor_only': return '#f59e0b';
      case 'admin_only': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getAccessLevelLabel(accessLevel: string): string {
    switch (accessLevel) {
      case 'public': return 'Public';
      case 'medical_staff': return 'Medical Staff';
      case 'doctor_only': return 'Doctor Only';
      case 'admin_only': return 'Admin Only';
      default: return 'Unknown';
    }
  }

  static getAccessLevelIcon(accessLevel: string): string {
    switch (accessLevel) {
      case 'public': return '🌍';
      case 'medical_staff': return '👥';
      case 'doctor_only': return '👨‍⚕️';
      case 'admin_only': return '🔒';
      default: return '❓';
    }
  }

  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // static getFileTypeIcon(mimeType: string): string {
  //   if (mimeType.startsWith('image/')) return '🖼️';
  //   if (mimeType.includes('pdf')) return '📄';
  //   if (mimeType.includes('word')) return '📝';
  //   if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  //   if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
  //   if (mimeType.startsWith('video/')) return '🎥';
  //   if (mimeType.startsWith('audio/')) return '🎵';
  //   return '📎';
  // }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  static isPDFFile(mimeType: string): boolean {
    return mimeType === 'application/pdf';
  }

  // static canPreview(mimeType: string): boolean {
  //   return this.isImageFile(mimeType) || this.isPDFFile(mimeType);
  // }

  // Update file validation
static validateFile(file: File): { isValid: boolean; error?: string } {
  // File size limit (50MB - DICOM files can be larger)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    return { isValid: false, error: 'File size must be less than 50MB' };
  }

  // Allowed file types
  const allowedTypes = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain', 'text/csv',
    'application/dicom', // ✅ Add DICOM
    'application/octet-stream', // ✅ DICOM sometimes detected as this
  ];

  // Check by extension if mime type detection fails
  const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'dcm'];
  const fileExtension = file.name.split('.').pop()?.toLowerCase();

  if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension || '')) {
    return { isValid: false, error: 'File type not supported' };
  }

  return { isValid: true };
}

// Update file type icon method
static getFileTypeIcon(mimeType: string, fileName?: string): string {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  
  if (mimeType === 'application/dicom' || extension === 'dcm') return '🏥';
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
  if (mimeType.startsWith('video/')) return '🎥';
  if (mimeType.startsWith('audio/')) return '🎵';
  return '📎';
}

// Add DICOM detection method
static isDicomFile(mimeType: string, fileName?: string): boolean {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return mimeType === 'application/dicom' || extension === 'dcm';
}

// Update preview capability
static canPreview(mimeType: string, fileName?: string): boolean {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  
  // DICOM files typically need special viewers
  if (mimeType === 'application/dicom' || extension === 'dcm') {
    return false; // Or true if you implement a DICOM viewer
  }
  
  return this.isImageFile(mimeType) || this.isPDFFile(mimeType);
}
}

export default DocumentService;